import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getOperationDashboardSummary,
  OperationDashboardSummary
} from '../OperationModule/shared/services/operation-dashboard.data';
import { isErrorInfo } from '../shared/interfaces/error-info.interface';

type MetricDefinition = {
  key: string;
  title: string;
  done: number;
  total: number;
};

function getPercent(done: number, total: number) {
  if (!total) return 0;
  return Math.min(100, Math.round((done / total) * 100));
}

function getPercentTone(percent: number) {
  if (percent >= 80) {
    return {
      accent: 'bg-emerald-50 text-emerald-700',
      progress: 'bg-emerald-500'
    };
  }

  if (percent >= 40) {
    return {
      accent: 'bg-amber-50 text-amber-700',
      progress: 'bg-amber-500'
    };
  }

  return {
    accent: 'bg-rose-50 text-rose-700',
    progress: 'bg-rose-500'
  };
}

export default function DailyOperationsContainer() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [summary, setSummary] = useState<OperationDashboardSummary | null>(null);

  const loadSummary = useCallback(() => {
    setLoading(true);
    setError(false);

    getOperationDashboardSummary().then((response) => {
      setLoading(false);

      if (isErrorInfo(response)) {
        setError(true);
        return;
      }

      setSummary(response);
    });
  }, []);

  useEffect(() => loadSummary(), []);

  const metrics = useMemo<MetricDefinition[]>(
    () => [
      {
        key: 'checkins',
        title: 'Check-ins',
        done: summary?.checkinsDone || 0,
        total: summary?.checkinsTotal || 0
      },
      {
        key: 'checkouts',
        title: 'Check-outs',
        done: summary?.checkoutsDone || 0,
        total: summary?.checkoutsTotal || 0
      },
      {
        key: 'housekeeping',
        title: 'Housekeeping',
        done: summary?.housekeepingDone || 0,
        total: summary?.housekeepingTotal || 0
      },
      {
        key: 'maintenance',
        title: 'Maintenance',
        done: summary?.MaintenanceDone || 0,
        total: summary?.MaintenanceTotal || 0
      }
    ],
    [summary]
  );
  const totalDone = useMemo(() => metrics.reduce((sum, item) => sum + item.done, 0), [metrics]);
  const totalTasks = useMemo(() => metrics.reduce((sum, item) => sum + item.total, 0), [metrics]);

  return (
    <div className="min-h-screen bg-creams-50 px-4 py-5 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1440px] pb-28">
        {loading ? (
          <div className="grid grid-cols-1 gap-9 md:grid-cols-2 xl:grid-cols-4">
            {[1, 2, 3, 4].map((item) => (
              <div
                key={item}
                className="h-[240px] animate-pulse rounded-3xl border border-gray-200 bg-white"
              />
            ))}
          </div>
        ) : error ? (
          <div className="rounded-3xl border border-rose-200 bg-white p-8 shadow-sm">
            <p className="text-base font-gilroy-bold uppercase tracking-[0.22em] text-rose-500">
              Daily Operations
            </p>
            <h1 className="mt-3 text-5xl font-recoleta text-neutral-500">
              Unable to load dashboard data
            </h1>
            <p className="mt-3 max-w-xl text-lg font-gilroy-medium text-neutral-400">
              The backend summary is temporarily unavailable. Retry to fetch the latest operation
              numbers.
            </p>
            <button
              type="button"
              onClick={loadSummary}
              className="mt-6 rounded-xl bg-neutral-500 px-5 py-3 text-sm font-gilroy-bold text-white transition hover:bg-neutral-600"
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            <section className="rounded-3xl border border-gray-200 bg-gradient-to-br from-white via-white to-primary-50/30 p-6 shadow-sm flex justify-between items-center gap-4">
              <p className="text-5xl font-recoleta font-semibold text-neutral-500">
                Daily Operations{' '}
                <span className="text-3xl  block ml-4 font-gilroy-medium font-semibold text-primary-400">
                  "Keep the day moving"
                </span>
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-lg font-gilroy-medium text-neutral-400">Completed</p>
                  <p className="mt-1 text-4xl font-gilroy-bold text-neutral-500">{totalDone}</p>
                </div>
                <div>
                  <p className="text-lg font-gilroy-medium text-neutral-400">Total</p>
                  <p className="mt-1 text-4xl font-gilroy-bold text-neutral-500">{totalTasks}</p>
                </div>
              </div>
            </section>

            <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              {metrics.map((metric) => {
                const percent = getPercent(metric.done, metric.total);
                const remaining = Math.max(metric.total - metric.done, 0);
                const tone = getPercentTone(percent);

                return (
                  <article
                    key={metric.key}
                    className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="mt-2 text-3xl font-medium font-recoleta text-neutral-500">
                        {metric.title}
                      </h3>
                      <span
                        className={`rounded-full px-3 py-1 text-xl font-medium font-gilroy-bold ${tone.accent}`}
                      >
                        {percent}%
                      </span>
                    </div>

                    <div className="mt-5 grid grid-cols-3 gap-3 items-end">
                      <div>
                        <p className="text-lg font-gilroy-medium text-neutral-400 font-medium">
                          Done
                        </p>
                        <p className="mt-1 text-5xl font-gilroy-bold text-neutral-500">
                          {metric.done}
                        </p>
                      </div>
                      <div>
                        <p className="text-lg font-gilroy-medium text-neutral-400 font-medium">
                          Total
                        </p>
                        <p className="mt-1 text-3xl font-gilroy-bold text-neutral-500">
                          {metric.total}
                        </p>
                      </div>
                      <div>
                        <p className="text-lg font-gilroy-medium text-neutral-400 font-medium">
                          Remaining
                        </p>
                        <p className="mt-1 text-3xl font-gilroy-bold text-neutral-500">
                          {remaining}
                        </p>
                      </div>
                    </div>

                    <div className="mt-5">
                      <div className="h-2 rounded-full bg-white/80">
                        <div
                          className={`h-2 rounded-full ${tone.progress}`}
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                    </div>
                  </article>
                );
              })}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
